import { supabase } from '@/lib/supabase'

export type BankAccount = {
  id?: string
  affiliate_user_id?: string
  holder_name: string
  document?: string | null
  method: 'pix' | 'ted'
  pix_key_type?: string | null
  pix_key_value?: string | null
  bank_code?: string | null
  bank_name?: string | null
  branch?: string | null
  account_number?: string | null
  account_type?: string | null
  is_default?: boolean
  verification_status?: string
}

export async function getMyBankAccount(): Promise<BankAccount | null> {
  const { data, error } = await supabase
    .from('affiliate_bank_accounts')
    .select('*')
    .eq('affiliate_user_id', (await supabase.auth.getUser()).data.user?.id || '')
    .eq('is_default', true)
    .maybeSingle()
  if (error && error.message && !String(error.message).includes('No rows')) throw error
  return (data as any) || null
}

export async function upsertMyBankAccount(payload: BankAccount): Promise<void> {
  const userId = (await supabase.auth.getUser()).data.user?.id
  if (!userId) throw new Error('not_authenticated')
  const row = {
    ...payload,
    affiliate_user_id: userId,
    is_default: true,
    verification_status: 'pending',
  }
  // Observação: há um índice único parcial (affiliate_user_id WHERE is_default=true).
  // O Postgres não permite inferência de índice parcial via lista de colunas no ON CONFLICT
  // pelo supabase-js. Para manter SSOT/KISS, fazemos update-if-exists, senão insert.
  const { data: existing, error: selErr } = await supabase
    .from('affiliate_bank_accounts')
    .select('id')
    .eq('affiliate_user_id', userId)
    .eq('is_default', true)
    .maybeSingle()
  if (selErr && selErr.message && !String(selErr.message).includes('No rows')) throw selErr

  if (existing?.id) {
    const { error } = await supabase
      .from('affiliate_bank_accounts')
      .update(row)
      .eq('id', existing.id)
    if (error) throw error
  } else {
    const { error } = await supabase
      .from('affiliate_bank_accounts')
      .insert(row)
    if (error) throw error
  }
}

export async function listMyBankAccounts(): Promise<BankAccount[]> {
  const userId = (await supabase.auth.getUser()).data.user?.id || ''
  if (!userId) return []
  const { data, error } = await supabase
    .from('affiliate_bank_accounts')
    .select('*')
    .eq('affiliate_user_id', userId)
    .order('is_default', { ascending: false })
    .order('updated_at', { ascending: false })
  if (error) throw error
  return (data as any) || []
}

export async function listBankAccountsOf(affiliateUserId: string): Promise<BankAccount[]> {
  const { data, error } = await supabase
    .from('affiliate_bank_accounts')
    .select('*')
    .eq('affiliate_user_id', affiliateUserId)
    .order('is_default', { ascending: false })
    .order('updated_at', { ascending: false })
  if (error) throw error
  return (data as any) || []
}

export async function insertMyBankAccount(payload: BankAccount): Promise<string> {
  const userId = (await supabase.auth.getUser()).data.user?.id
  if (!userId) throw new Error('not_authenticated')
  const row = { ...payload, affiliate_user_id: userId, is_default: !!payload.is_default, verification_status: 'pending' }
  const { data, error } = await supabase.from('affiliate_bank_accounts').insert(row).select('id').single()
  if (error) throw error
  return (data as any)?.id
}

export async function updateMyBankAccount(id: string, patch: Partial<BankAccount>): Promise<void> {
  const userId = (await supabase.auth.getUser()).data.user?.id
  if (!userId) throw new Error('not_authenticated')
  const { error } = await supabase
    .from('affiliate_bank_accounts')
    .update({ ...patch })
    .eq('id', id)
    .eq('affiliate_user_id', userId)
  if (error) throw error
}

export async function deleteMyBankAccount(id: string): Promise<void> {
  const userId = (await supabase.auth.getUser()).data.user?.id
  if (!userId) throw new Error('not_authenticated')
  const { error } = await supabase
    .from('affiliate_bank_accounts')
    .delete()
    .eq('id', id)
    .eq('affiliate_user_id', userId)
  if (error) throw error
}

export async function setDefaultMyBankAccount(id: string): Promise<void> {
  const userId = (await supabase.auth.getUser()).data.user?.id
  if (!userId) throw new Error('not_authenticated')
  // Primeiro, remover default atual
  await supabase
    .from('affiliate_bank_accounts')
    .update({ is_default: false })
    .eq('affiliate_user_id', userId)
    .eq('is_default', true)
    .neq('id', id)
  const { error } = await supabase
    .from('affiliate_bank_accounts')
    .update({ is_default: true })
    .eq('id', id)
    .eq('affiliate_user_id', userId)
  if (error) throw error
}
